import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaGithub, FaGlobe, FaCode, FaImage, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const Projects = ({ darkMode }) => {
    const { t } = useTranslation();
    const [, setImagesLoaded] = useState(false);
    const imagesRef = useRef({});
    // Estado para controlar qué proyectos tienen la lista de tecnologías expandida
    const [expandedTechs, setExpandedTechs] = useState({});

    // Función para alternar la expansión de tecnologías de un proyecto
    const toggleTechsExpansion = (projectId) => {
        setExpandedTechs(prev => ({
            ...prev,
            [projectId]: !prev[projectId]
        }));
    };

    // Usar traducciones para los proyectos - usando useCallback para resolver la advertencia
    const getLocalizedProject = useCallback((projectId) => {
        try {
            const translatedProjects = t('projects.items', { returnObjects: true }) || [];
            // Asegurar que translatedProjects es un array
            if (Array.isArray(translatedProjects)) {
                return translatedProjects.find(p => p.id === projectId);
            }
            return null;
        } catch (error) {
            console.error("Error al obtener traducción:", error);
            return null;
        }
    }, [t]);

    // Datos base de los proyectos
    const baseProjectsData = useMemo(() => [
        {
            id: 1,
            title: "Chatbot CRM con IA - Hytech",
            description: "Plataforma de chatbot inteligente para CRM desarrollada en Hytech con Streamlit. Permite búsquedas en base de datos MySQL utilizando lenguaje natural mediante LangChain con tools personalizadas para consultas SQL automáticas.",
            technologies: ["Python", "LangChain", "OpenAI", "MySQL", "RAG", "AI Agents", "NLP", "SQL Tools", "Streamlit", "FastAPI"],
            image: `${process.env.PUBLIC_URL}/images/ai-crm-chatbot.jpg`,
            github: null,
            live: null,
            featured: true
        },
        {
            id: 2,
            title: "Sistema de Reportes con IA - Hytech",
            description: "Software de reportes empresariales desarrollado en Hytech con IA para análisis y generación automática de informes. Implementado con Streamlit para interfaces interactivas y FastAPI para servicios backend robustos.",
            technologies: ["Python", "LangChain", "OpenAI", "LLMs", "Data Analysis", "Pandas", "Report Generation", "Streamlit", "FastAPI"],
            image: `${process.env.PUBLIC_URL}/images/ai-reporting-system.jpg`,
            github: null,
            live: null,
            featured: true
        },
        {
            id: 3,
            title: "Blog Web con Java",
            description: "Blog web desarrollado con Java, JDBC para persistencia en H2, JPA y Hibernate para ORM. Frontend con Bootstrap. Incluye websockets para chat y Ajax para paginación.",
            technologies: ["Java", "JDBC", "JPA", "Hibernate", "Bootstrap", "Websockets", "Ajax"],
            image: `${process.env.PUBLIC_URL}/images/blog-project.jpg`,
            github: null,
            live: null,
            featured: true
        },
        {
            id: 4,
            title: "Aplicación Web Móvil",
            description: "Aplicación web adaptable que utiliza HTML5 con LocalStorage, Geolocator e IndexedDB para capturar información sin conexión a internet y sincronizar cuando haya conectividad.",
            technologies: ["HTML5", "CSS3", "JavaScript", "LocalStorage", "IndexedDB", "ServiceWorker"],
            image: `${process.env.PUBLIC_URL}/images/pwa-project.jpg`,
            github: null,
            live: null,
            featured: true
        },
        {
            id: 5,
            title: "Acortador de URLs",
            description: "Servicio de acortamiento de URLs con APIs REST y SOAP, esquema de seguridad JWT y MongoDB para persistencia. Desplegado con proxy inverso y configuración de DNS personalizado.",
            technologies: ["MongoDB", "JWT", "REST API", "SOAP", "Proxy Inverso", "DNS"],
            image: `${process.env.PUBLIC_URL}/images/url-shortener.jpg`,
            github: null,
            live: null,
            featured: true
        },
        {
            id: 6,
            title: "Chatbot para Telegram",
            description: "Desarrollo de chatbot para Telegram utilizando Aiogram (Python) durante mi trabajo en Click Ping. Integración con APIs externas.",
            technologies: ["Python", "Django", "Aiogram", "API REST", "Telegram Bot API"],
            image: `${process.env.PUBLIC_URL}/images/telegram-bot.jpg`,
            github: null,
            live: null,
            featured: false
        },
        {
            id: 7,
            title: "Sistema de Gestión",
            description: "Sistema de gestión empresarial con PostgreSQL para almacenamiento de datos. Incluye reportes personalizados y panel de administración.",
            technologies: ["Python", "Django", "PostgreSQL", "Docker", "Bootstrap"],
            image: `${process.env.PUBLIC_URL}/images/management-system.jpg`,
            github: null,
            featured: false
        },
        {
            id: 8,
            title: "Pokedex con Flutter",
            description: "Aplicación móvil que consume la API de Pokemon para mostrar información detallada sobre diferentes Pokémon, sus estadísticas, evoluciones y habilidades. Implementa búsqueda avanzada, favoritos y comparaciones entre Pokémon.",
            technologies: ["Flutter", "Dart", "API REST", "SQLite", "Provider", "Animaciones", "Firebase", "Estado Global", "Clean Architecture", "Repositorio Pattern", "SOLID", "Unit Testing"],
            image: `${process.env.PUBLIC_URL}/images/pokedex-flutter.jpg`,
            github: null,
            live: null,
            featured: true
        },
        {
            id: 9,
            title: "App de Préstamos",
            description: "Aplicación móvil para gestión de préstamos personales. Permite registrar clientes, préstamos, pagos y genera reportes de estado. Incluye autenticación de usuarios y sincronización con la nube.",
            technologies: ["Flutter", "Dart", "Firebase", "Firestore", "Firebase Auth", "PDF Generation", "Provider", "Notificaciones Push", "Google Maps", "Bloc Pattern", "Getx", "Chart.js"],
            image: `${process.env.PUBLIC_URL}/images/loan-app-flutter.jpg`,
            github: null,
            live: null,
            featured: false
        }
    ], []);

    // Combinar datos base con traducciones
    const projectsData = useMemo(() => {
        return baseProjectsData.map(project => {
            const localizedProject = getLocalizedProject(project.id);
            if (localizedProject) {
                return {
                    ...project,
                    title: localizedProject.title || project.title,
                    description: localizedProject.description || project.description
                };
            }
            return project; // Usar los datos predeterminados si no hay traducción
        });
    }, [baseProjectsData, getLocalizedProject]);

    // Precarga de imágenes simplificada
    useEffect(() => {
        let loadedCount = 0;
        const totalImages = projectsData.length;
        const imagePromises = [];

        // Por defecto, mostrar placeholders
        setImagesLoaded(false);

        // Intentar cargar cada imagen original
        projectsData.forEach(project => {
            const promise = new Promise((resolve) => {
                const img = new Image();

                // Si la imagen carga correctamente
                img.onload = () => {
                    loadedCount++;
                    imagesRef.current[project.id] = true;
                    resolve();

                    // Cuando todas las imágenes estén cargadas, actualizar el estado
                    if (loadedCount === totalImages) {
                        setImagesLoaded(true);
                    }
                };

                // Si la imagen falla al cargar
                img.onerror = () => {
                    loadedCount++;
                    imagesRef.current[project.id] = false;
                    resolve();

                    if (loadedCount === totalImages) {
                        setImagesLoaded(true);
                    }
                };

                // Timeout para evitar esperas muy largas
                setTimeout(() => {
                    if (!imagesRef.current.hasOwnProperty(project.id)) {
                        imagesRef.current[project.id] = false;
                        loadedCount++;
                        resolve();

                        if (loadedCount === totalImages) {
                            setImagesLoaded(true);
                        }
                    }
                }, 2000); // 2 segundos de timeout

                // Iniciar la carga de la imagen
                img.src = project.image;
            });

            imagePromises.push(promise);
        });

        // Timeout general para mostrar la interfaz incluso si algunas imágenes tardan demasiado
        const timeoutPromise = new Promise(resolve => {
            setTimeout(() => {
                setImagesLoaded(true);
                resolve();
            }, 2500); // 2.5 segundos máximo para mostrar interfaz
        });

        // Esperar a que todas las promesas se resuelvan
        Promise.all([...imagePromises, timeoutPromise]);

        // Limpieza al desmontar
        return () => {
            imagesRef.current = {};
        };
    }, [projectsData]);

    const [activeTab, setActiveTab] = useState('all');

    // Filtrar proyectos según la pestaña activa
    const filteredProjects = useMemo(() => {
        return activeTab === 'all'
            ? projectsData
            : activeTab === 'featured'
                ? projectsData.filter(project => project.featured)
                : projectsData.filter(project => !project.featured);
    }, [activeTab, projectsData]);

    // Componente para la imagen de placeholder
    const PlaceholderImage = ({ title }) => (
        <div className={`flex items-center justify-center w-full h-full ${
            darkMode ? 'bg-gray-600' : 'bg-gray-200'
        }`}>
            <div className="text-center p-4">
                <FaImage className={`mx-auto text-4xl ${
                    darkMode ? 'text-cyan-400' : 'text-cyan-600'
                }`} />
                <p className={`mt-2 font-medium ${
                    darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>{title}</p>
            </div>
        </div>
    );

    // Componente para carga perezosa de imágenes
    const LazyImage = ({ project }) => {
        const imageLoaded = imagesRef.current[project.id];

        return (
            <div className="w-full h-full">
                {/* Si la imagen está cargada, mostrarla */}
                {imageLoaded && (
                    <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        width="400"
                        height="267"
                    />
                )}

                {/* Si la imagen no está cargada, mostrar placeholder */}
                {!imageLoaded && (
                    <PlaceholderImage title={project.title} />
                )}
            </div>
        );
    };

    return (
        <section id="projects" className={`py-8 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-8 shadow-lg`}>
            <h2 className={`text-3xl font-bold mb-8 text-center ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                {t('projects.title')}
            </h2>

            <div className="flex justify-center mb-8">
                <div className={`inline-flex rounded-md shadow-sm p-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <button
                        onClick={() => setActiveTab('all')}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                            activeTab === 'all'
                                ? darkMode
                                    ? 'bg-cyan-600 text-white'
                                    : 'bg-cyan-500 text-white'
                                : darkMode
                                    ? 'text-gray-300 hover:bg-gray-600'
                                    : 'text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        {t('projects.tabs.all')}
                    </button>
                    <button
                        onClick={() => setActiveTab('featured')}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                            activeTab === 'featured'
                                ? darkMode
                                    ? 'bg-cyan-600 text-white'
                                    : 'bg-cyan-500 text-white'
                                : darkMode
                                    ? 'text-gray-300 hover:bg-gray-600'
                                    : 'text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        {t('projects.tabs.featured')}
                    </button>
                    <button
                        onClick={() => setActiveTab('other')}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                            activeTab === 'other'
                                ? darkMode
                                    ? 'bg-cyan-600 text-white'
                                    : 'bg-cyan-500 text-white'
                                : darkMode
                                    ? 'text-gray-300 hover:bg-gray-600'
                                    : 'text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        {t('projects.tabs.other')}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProjects.map((project) => (
                    <motion.div
                        key={project.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        whileHover={{ y: -5, transition: { duration: 0.2 } }}
                        className={`rounded-lg overflow-hidden shadow-lg ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-50'
                        }`}
                    >
                        <div className="h-48 overflow-hidden">
                            <LazyImage project={project} />
                        </div>

                        <div className="p-4">
                            <h3 className="text-xl font-bold mb-2">{project.title}</h3>
                            <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                {project.description}
                            </p>

                            <div className="mb-4">
                                <div className="flex flex-wrap gap-2 mb-2">
                                    {/* Mostrar primeros 4 tecnologías o todas si está expandido */}
                                    {(expandedTechs[project.id] ? project.technologies : project.technologies.slice(0, 4)).map((tech, index) => (
                                        <span
                                            key={index}
                                            className={`text-xs px-2 py-1 rounded-full ${
                                                darkMode
                                                    ? 'bg-gray-600 text-gray-200'
                                                    : 'bg-gray-200 text-gray-700'
                                            }`}
                                        >
                                            {tech}
                                        </span>
                                    ))}
                                </div>

                                {/* Mostrar botón "Ver más" solo si hay más de 4 tecnologías */}
                                {project.technologies.length > 4 && (
                                    <button
                                        onClick={() => toggleTechsExpansion(project.id)}
                                        className={`flex items-center text-xs mt-2 ${
                                            darkMode ? 'text-cyan-400 hover:text-cyan-300' : 'text-cyan-600 hover:text-cyan-700'
                                        }`}
                                    >
                                        {expandedTechs[project.id] ? (
                                            <>
                                                {t('projects.viewLess')} <FaChevronUp className="ml-1" />
                                            </>
                                        ) : (
                                            <>
                                                {t('projects.viewMore', { count: project.technologies.length - 4 })} <FaChevronDown className="ml-1" />
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>

                            <div className="flex justify-between">
                                {project.github && (
                                    <a
                                        href={project.github}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`flex items-center gap-1 text-sm ${
                                            darkMode ? 'text-cyan-400 hover:text-cyan-300' : 'text-cyan-600 hover:text-cyan-700'
                                        }`}
                                    >
                                        <FaGithub /> {t('projects.code')}
                                    </a>
                                )}

                                {project.live && (
                                    <a
                                        href={project.live}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`flex items-center gap-1 text-sm ${
                                            darkMode ? 'text-cyan-400 hover:text-cyan-300' : 'text-cyan-600 hover:text-cyan-700'
                                        }`}
                                    >
                                        <FaGlobe /> {t('projects.demo')}
                                    </a>
                                )}

                                {!project.github && !project.live && (
                                    <span className={`flex items-center gap-1 text-sm ${
                                        darkMode ? 'text-gray-400' : 'text-gray-500'
                                    }`}>
                                        <FaCode /> {t('projects.privateProject')}
                                    </span>
                                )}
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>
        </section>
    );
};

export default Projects;